// src/pages/fleet/admin/IfmsSettingsPage.tsx — Administration → IFMS.
//
// The state of the government reporting link, on one screen. Everything here is
// read-only on purpose: the Client Id and Secret live in the server's .env and are
// never shown or edited from a browser, so this page answers "is it working?" and
// "what is left to do?" rather than offering a form.
//
// It is also where go-live is confirmed — Test Connection performs the real
// handshake with IFMS without reporting any trip.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getIfmsStatus, testIfms } from '../../../services/fleetApi';
import type { IfmsStatus, IfmsTestResult } from '../../../services/fleetApi';
import { P } from '../fleetStyles';
import Icon from '../Icon';
import { apiError } from '../../../utils/errors';
import { css } from '../../../utils/css';

const fmtDate = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/** The one line that says where we stand, and the colour that goes with it. */
function overall(s: IfmsStatus): { tone: 'green' | 'orange' | 'red'; title: string; detail: string } {
  if (s.reportingStarted) {
    return { tone: 'green', title: 'Reporting to IFMS is live',
      detail: `Trips are being reported to ${s.environment}. Every trip must reach IFMS within ${s.slaHours} hours of being created.` };
  }
  if (s.configured) {
    return { tone: 'orange', title: 'Connected, reporting starts later',
      detail: `Credentials are set for ${s.environment}, but reporting begins on ${fmtDate(s.reportingFrom)}. Trips created before that date are kept for our records only.` };
  }
  return { tone: 'orange', title: 'Reporting has not started yet',
    detail: 'IFMS credentials are not set on this server. Trips still build and store their full IFMS payload, so nothing is lost — but nothing is sent, no deadline applies, and no alert is raised.' };
}

const TONE: Record<string, { fg: string; bg: string }> = {
  green:  { fg: 'var(--fx-green)',  bg: 'var(--fx-green-soft)' },
  orange: { fg: 'var(--fx-orange)', bg: 'var(--fx-orange-soft)' },
  red:    { fg: 'var(--fx-red)',    bg: 'var(--fx-red-soft)' },
};

export default function IfmsSettingsPage() {
  const navigate = useNavigate();
  const [s, setS] = useState<IfmsStatus | null>(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<IfmsTestResult | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getIfmsStatus()
      .then((r) => { setS(r.data.ifms); setErr(''); })
      .catch((e) => setErr(apiError(e, 'Could not load the IFMS status')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await testIfms();
      setResult(r.data.result);
    } catch (e) {
      setResult({ ok: false, step: 'network', message: apiError(e, 'The test could not be run') });
    } finally {
      setTesting(false);
    }
  };

  const o = s ? overall(s) : null;

  return (
    <div style={P.page}>
      <div style={P.head}>
        <div style={S.headLeft}>
          <div style={S.headIcon}><Icon name="shield" size={19} stroke={2.1} /></div>
          <div>
            <h1 style={P.h1}>IFMS</h1>
            <p style={P.sub}>Government reporting link — status, credentials and go-live</p>
          </div>
        </div>
        <button type="button" style={P.btnGhost} onClick={load} disabled={loading}>
          <Icon name="refresh" size={14} stroke={2.2} /> Refresh
        </button>
      </div>

      {err && <div style={S.err}>{err}</div>}
      {loading && !s && <div style={S.muted}>Loading…</div>}

      {s && o && (
        <>
          {/* ── Where we stand ── */}
          <div style={{ ...S.banner, background: TONE[o.tone].bg, borderColor: TONE[o.tone].fg }}>
            <div style={{ ...S.bannerDot, background: TONE[o.tone].fg }} />
            <div>
              <div style={{ ...S.bannerTitle, color: TONE[o.tone].fg }}>{o.title}</div>
              <div style={S.bannerText}>{o.detail}</div>
            </div>
          </div>

          {/* ── Connection ── */}
          <Panel title="Connection" sub="Set in the server's .env file — never from this screen">
            <Row label="Environment" value={s.environment} />
            <Row label="Base URL" value={s.baseUrl || 'Not set'} mono missing={!s.baseUrl} />
            <Row label="Client Id" value={s.clientId || 'Not set'} mono missing={!s.clientId} />
            <Row label="Client Secret" value={s.secretSet ? 'Set' : 'Not set'} missing={!s.secretSet} />
            <Row label="Reporting starts" value={s.reportingFrom ? fmtDate(s.reportingFrom) : 'Not set'} missing={!s.reportingFrom} />
            <Row label="Delivery deadline" value={`${s.slaHours} hours from trip creation`} />

            <div style={S.testRow}>
              <button type="button" style={P.btnPrimary} onClick={runTest} disabled={testing}>
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
              <span style={S.hint}>
                Performs the real handshake with IFMS. No trip is reported, so this is safe to run any time.
              </span>
            </div>
            {result && (
              <div style={{ ...S.result, background: result.ok ? 'var(--fx-green-soft)' : 'var(--fx-red-soft)',
                            borderColor: result.ok ? 'var(--fx-green)' : 'var(--fx-red)',
                            color: result.ok ? 'var(--fx-green)' : 'var(--fx-red)' }}>
                <b>{result.ok ? '✓ ' : '✗ '}{result.message}</b>
                {(result.httpStatus || result.ms != null) && (
                  <div style={S.resultMeta}>
                    {result.httpStatus ? `HTTP ${result.httpStatus}` : ''}
                    {result.httpStatus && result.ms != null ? ' · ' : ''}
                    {result.ms != null ? `${result.ms} ms` : ''}
                  </div>
                )}
              </div>
            )}
          </Panel>

          {/* ── The three APIs ── */}
          <Panel title="The three calls" sub="Sent automatically from the truck's own GPS — no button anywhere">
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Call</th>
                  <th style={S.th}>Endpoint</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Delivered</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {s.apis.map((a) => (
                  <tr key={a.api}>
                    <td style={S.td}><b>{a.label}</b></td>
                    <td style={{ ...S.td, fontFamily: 'DM Mono, monospace', fontSize: 12, color: 'var(--fx-text-2)' }}>{a.path}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: 'var(--fx-green)', fontWeight: 600 }}>{a.success}</td>
                    <td style={{ ...S.td, textAlign: 'right', color: a.pending ? 'var(--fx-orange)' : 'var(--fx-text-3)', fontWeight: 600 }}>{a.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={S.hint}>Counts cover the last 7 days. {s.tripsLastWeek} trip(s) started in that period.</div>
          </Panel>

          {/* ── What needs attention ── */}
          <Panel title="Needs attention" sub="Anything IFMS has not accepted">
            {s.escalated > 0 ? (
              <div style={S.escalated}>
                <b>{s.escalated} call(s) missed the {s.slaHours}-hour deadline.</b> These are retried on their own, but
                something is blocking them — open the queue to see the reason for each.
              </div>
            ) : (
              <div style={S.ok}>Nothing is overdue.</div>
            )}
            <button type="button" style={{ ...P.btnGhost, marginTop: 12 }} onClick={() => navigate('/fleet/ifms')}>
              Open IFMS Queue <Icon name="chevronRight" size={14} stroke={2.2} />
            </button>
          </Panel>

          {!s.configured && (
            <Panel title="To start reporting" sub="What an administrator has to put on the server">
              <ol style={S.steps}>
                <li>Get the <b>Client Id</b> and <b>Client Secret</b> from the IFMS / VLTS team (staging first).</li>
                <li>Put them in <code style={S.code}>backend/.env</code> as <code style={S.code}>IFMS_CLIENT_ID</code> and <code style={S.code}>IFMS_CLIENT_SECRET</code>, with <code style={S.code}>IFMS_BASE_URL</code>.</li>
                <li>Set <code style={S.code}>IFMS_REPORTING_FROM</code> to the first day trips should be reported. Trips created before it are never sent.</li>
                <li>Restart the server, come back here and press <b>Test Connection</b>.</li>
              </ol>
              <div style={S.hint}>
                Until then every trip still builds and stores its full IFMS payload, so nothing is lost and no false
                alerts are raised while the fleet is being run in.
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  );
}

function Panel({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <section style={S.panel}>
      <div style={S.panelHead}>
        <h2 style={S.panelTitle}>{title}</h2>
        {sub && <p style={S.panelSub}>{sub}</p>}
      </div>
      <div style={S.panelBody}>{children}</div>
    </section>
  );
}

function Row({ label, value, mono, missing }: { label: string; value: string; mono?: boolean; missing?: boolean }) {
  return (
    <div style={S.row}>
      <span style={S.rowLabel}>{label}</span>
      <span style={{
        ...S.rowValue,
        ...(mono ? { fontFamily: 'DM Mono, monospace', fontSize: 12.5 } : {}),
        ...(missing ? { color: 'var(--fx-orange)' } : {}),
      }}>{value}</span>
    </div>
  );
}

const S = css({
  headLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  headIcon: {
    width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)', flexShrink: 0,
  },
  err:   { color: 'var(--fx-red)', background: 'var(--fx-red-soft)', borderRadius: 10, fontSize: 13, padding: '10px 14px', marginBottom: 14 },
  muted: { color: 'var(--fx-text-2)', fontSize: 13, padding: '20px 2px' },

  banner: { display: 'flex', gap: 12, alignItems: 'flex-start', border: '1px solid', borderRadius: 12, padding: '13px 16px', marginBottom: 16 },
  bannerDot: { width: 9, height: 9, borderRadius: 9, marginTop: 5, flexShrink: 0 },
  bannerTitle: { fontSize: 14, fontWeight: 700, marginBottom: 3 },
  bannerText: { fontSize: 12.5, lineHeight: 1.55, color: 'var(--fx-text-2)' },

  panel: { background: 'var(--fx-card)', border: '1px solid var(--fx-border)', borderRadius: 14, marginBottom: 16, overflow: 'hidden' },
  panelHead: { padding: '13px 18px', borderBottom: '1px solid var(--fx-border)', background: 'var(--fx-gray-soft)' },
  panelTitle: { fontSize: 14, fontWeight: 700, margin: 0, color: 'var(--fx-text)' },
  panelSub: { fontSize: 11.5, color: 'var(--fx-text-2)', margin: '3px 0 0' },
  panelBody: { padding: '14px 18px 18px' },

  row: { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--fx-border)' },
  rowLabel: { fontSize: 12.5, color: 'var(--fx-text-2)' },
  rowValue: { fontSize: 13, fontWeight: 600, color: 'var(--fx-text)', textAlign: 'right', wordBreak: 'break-all' },

  testRow: { display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 },
  result: { border: '1px solid', borderRadius: 10, padding: '10px 13px', marginTop: 12, fontSize: 12.5, lineHeight: 1.5 },
  resultMeta: { fontSize: 11, opacity: 0.8, marginTop: 4, fontWeight: 400 },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--fx-text-2)', textTransform: 'uppercase', letterSpacing: 0.4, padding: '0 0 8px' },
  td: { fontSize: 13, color: 'var(--fx-text)', padding: '9px 0', borderTop: '1px solid var(--fx-border)' },

  escalated: { fontSize: 12.5, lineHeight: 1.55, color: 'var(--fx-red)', background: 'var(--fx-red-soft)', border: '1px solid var(--fx-red)', borderRadius: 10, padding: '10px 13px' },
  ok: { fontSize: 13, color: 'var(--fx-green)', fontWeight: 600 },

  steps: { margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.85, color: 'var(--fx-text)' },
  code: { fontFamily: 'DM Mono, monospace', fontSize: 11.5, background: 'var(--fx-gray-soft)', padding: '1px 5px', borderRadius: 5 },
  hint: { fontSize: 11.5, color: 'var(--fx-text-2)', lineHeight: 1.55, marginTop: 10 },
});
